import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { UserProfileResponse } from "@bw/shared/types/generated";

export type PlayFormat = NonNullable<UserProfileResponse["play_formats"]>[number];
export type SkillLevel = NonNullable<UserProfileResponse["skill_level"]>;

// The real PlayFormat / SkillLevel enum values from the profile contract.
export const PLAY_FORMAT_OPTIONS: ReadonlyArray<{ value: PlayFormat; label: string }> = [
  { value: "singles", label: "Singles" },
  { value: "doubles", label: "Doubles" },
  { value: "mixed", label: "Mixed" },
];

export const SKILL_LEVEL_OPTIONS: ReadonlyArray<{ value: SkillLevel; label: string; detail: string }> = [
  { value: "beginner", label: "Beginner", detail: "New or returning" },
  { value: "intermediate", label: "Intermediate", detail: "Rallies comfortably, knows doubles rotation" },
  { value: "advanced", label: "Advanced", detail: "Plays competitively" },
];

export const PLAY_FORMAT_LABELS: Record<PlayFormat, string> = Object.fromEntries(
  PLAY_FORMAT_OPTIONS.map((option) => [option.value, option.label]),
) as Record<PlayFormat, string>;

export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = Object.fromEntries(
  SKILL_LEVEL_OPTIONS.map((option) => [option.value, option.label]),
) as Record<SkillLevel, string>;

/**
 * Keeps a native radio in the label for keyboard and screen readers while the
 * styled label shows the choice. (In sx, `width: 1` would mean 100%.)
 */
export const HIDDEN_RADIO_SX = {
  position: "absolute",
  opacity: 0,
  width: "1px",
  height: "1px",
  m: 0,
  p: 0,
  overflow: "hidden",
} as const;

function FieldTitle({ id, children }: { id: string; children: string }) {
  return (
    <Typography id={id} component="h3" sx={{ fontSize: "1rem", fontWeight: 700 }}>
      {children}
    </Typography>
  );
}

interface PlayFormatChipsProps {
  id: string;
  value: PlayFormat[] | undefined;
  onChange: (next: PlayFormat[]) => void;
}

/** Toggle chips for the events a player plays; any combination, or none. */
export function PlayFormatChips({ id, value, onChange }: PlayFormatChipsProps) {
  const current = value ?? [];
  return (
    <Stack spacing={1} role="group" aria-labelledby={id}>
      <FieldTitle id={id}>Events you play</FieldTitle>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {PLAY_FORMAT_OPTIONS.map((option) => {
          const selected = current.includes(option.value);
          return (
            <Chip
              key={option.value}
              label={option.label}
              clickable
              aria-pressed={selected}
              color={selected ? "primary" : "default"}
              variant={selected ? "filled" : "outlined"}
              onClick={() =>
                onChange(
                  selected
                    ? current.filter((format) => format !== option.value)
                    : // Keep the contract's order regardless of tap order.
                      PLAY_FORMAT_OPTIONS.map((o) => o.value).filter(
                        (format) => format === option.value || current.includes(format),
                      ),
                )
              }
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

interface SkillLevelPickerProps {
  id: string;
  value: SkillLevel | null | undefined;
  onChange: (next: SkillLevel) => void;
}

/** A single-choice list of self-described levels (native radios, so arrow keys work). */
export function SkillLevelPicker({ id, value, onChange }: SkillLevelPickerProps) {
  return (
    <Stack spacing={0.5}>
      <FieldTitle id={id}>Your level</FieldTitle>
      <RadioGroup
        aria-labelledby={id}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value as SkillLevel)}
      >
        {SKILL_LEVEL_OPTIONS.map((option) => {
          const selected = value === option.value;
          return (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={
                <Radio sx={HIDDEN_RADIO_SX} />
              }
              label={
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                  <Box sx={{ width: 24, display: "grid", placeItems: "center", color: "primary.main" }}>
                    {selected ? <CheckCircleRounded fontSize="small" /> : null}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>{option.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.detail}
                    </Typography>
                  </Box>
                </Stack>
              }
              sx={{
                position: "relative",
                m: 0,
                py: 1,
                px: 1,
                borderRadius: 2,
                "&:hover": { bgcolor: "action.hover" },
                "&:has(input:focus-visible)": { outline: 2, outlineColor: "primary.main", outlineOffset: -2 },
              }}
            />
          );
        })}
      </RadioGroup>
    </Stack>
  );
}
